/**
 * Sidebar - Navigation Consolidation
 * 7 primary items + 3 utility items
 */
import { useState, useEffect } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  TrendingUp,
  Search as SearchIcon,
  Briefcase,
  Filter,
  FlaskConical,
  Brain,
  Bell,
  Star,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Hexagon
} from 'lucide-react'
import clsx from 'clsx'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore } from '../../stores/authStore'
import { useConnectionStatus, type OverallStatus } from '../../hooks'
import { Tooltip } from '../ui/Tooltip'
import './Sidebar.css'

interface NavItem {
  path: string
  icon: typeof LayoutDashboard
  label: string
}

// PRIMARY NAV - 7 items always visible
const PRIMARY_NAV: NavItem[] = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/markets', icon: TrendingUp, label: 'Markets' },
  { path: '/research', icon: SearchIcon, label: 'Research' },
  { path: '/portfolio', icon: Briefcase, label: 'Portfolio' },
  { path: '/screener', icon: Filter, label: 'Screener' },
  { path: '/strategy', icon: FlaskConical, label: 'Strategy' },
  { path: '/intelligence', icon: Brain, label: 'Intelligence' },
]

// UTILITY NAV - 3 items at bottom
const UTILITY_NAV: NavItem[] = [
  { path: '/alerts', icon: Bell, label: 'Alerts' },
  { path: '/watchlist', icon: Star, label: 'Watchlist' },
  { path: '/settings', icon: Settings, label: 'Settings' },
]

const statusLabels: Record<OverallStatus, string> = {
  connected: 'Live',
  connecting: 'Connecting...',
  partial: 'Partial',
  disconnected: 'Offline'
}

function ConnectionStatusIndicator({ collapsed }: { collapsed: boolean }) {
  const { overall } = useConnectionStatus()

  // Only show status when connected - hide "Connecting..." and "Offline" states
  // to avoid making the app feel broken when WebSocket isn't available
  if (overall !== 'connected') {
    return null
  }

  return (
    <div className={clsx('connection-status', `connection-status--${overall}`)}>
      <div className="connection-status__dot" />
      {!collapsed && <span className="connection-status__label">{statusLabels[overall]}</span>}
    </div>
  )
}

function UserMenu({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [showMenu, setShowMenu] = useState(false)

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="user-menu-container">
      <button
        className="user-menu-trigger"
        onClick={() => setShowMenu(!showMenu)}
        aria-label="User menu"
      >
        <div className="user-avatar">
          {getInitials(user?.full_name || 'U')}
        </div>
        {!collapsed && (
          <div className="user-info">
            <span className="user-name">{user?.full_name}</span>
            <span className="user-tier">{
                  user?.subscription_tier === 'apex_pro' ? 'Cipher Pro' :
                  user?.subscription_tier === 'strategic' ? 'Strategic' :
                  user?.subscription_tier === 'informed' ? 'Informed' : 'Free'
                }</span>
          </div>
        )}
      </button>

      {showMenu && (
        <>
          <div className="user-menu-backdrop" onClick={() => setShowMenu(false)} />
          <div className={clsx('user-menu', collapsed && 'user-menu-collapsed')}>
            <NavLink
              to="/settings"
              className="user-menu-item"
              onClick={() => setShowMenu(false)}
            >
              <Settings size={16} />
              <span>Settings</span>
            </NavLink>
            <button className="user-menu-item logout" onClick={handleLogout}>
              <LogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </>
      )}
    </div>
  )
}

interface NavItemComponentProps {
  item: NavItem
  collapsed: boolean
  isActive?: boolean
  isUtility?: boolean
}

function NavItemComponent({ item, collapsed, isActive, isUtility }: NavItemComponentProps) {
  const { path, icon: Icon, label } = item

  return (
    <NavLink
      to={path}
      className={({ isActive: linkActive }) =>
        clsx(
          'sidebar-nav-item',
          isUtility && 'sidebar-nav-item-utility',
          (isActive || linkActive) && 'sidebar-nav-item-active'
        )
      }
      title={collapsed ? label : undefined}
      end={path === '/'}
    >
      <Icon size={18} className="sidebar-nav-icon" />
      {!collapsed && <span className="sidebar-nav-label">{label}</span>}
    </NavLink>
  )
}

export function Sidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { sidebarCollapsed, toggleSidebar } = useUIStore()

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('cipher_sidebar_collapsed', String(sidebarCollapsed))
  }, [sidebarCollapsed])

  // Check active states for routes with nested paths
  const isResearchActive = location.pathname.startsWith('/research') ||
                           location.pathname.startsWith('/stock/') ||
                           location.pathname.startsWith('/equities/')

  const isMarketsActive = location.pathname.startsWith('/markets') ||
                          location.pathname === '/macro' ||
                          location.pathname === '/fixed-income' ||
                          location.pathname === '/etf' ||
                          location.pathname === '/dividends' ||
                          location.pathname === '/market-internals'

  const isIntelligenceActive = location.pathname.startsWith('/intelligence') ||
                               location.pathname === '/ai' ||
                               location.pathname === '/news-impact' ||
                               location.pathname === '/causal-network'

  const isStrategyActive = location.pathname.startsWith('/strategy') ||
                           location.pathname === '/backtest' ||
                           location.pathname === '/strategy-studio'

  return (
    <aside className={clsx('sidebar', sidebarCollapsed && 'sidebar-collapsed')}>
      {/* Brand Mark */}
      <div className="sidebar-header">
        <button
          className="sidebar-logo"
          onClick={() => navigate('/')}
          aria-label="Go to Dashboard"
        >
          <div className="logo-icon">
            <Hexagon size={24} />
          </div>
          {!sidebarCollapsed && (
            <div className="logo-text-container">
              <span className="logo-text">CipherEquity</span>
              <span className="logo-subtitle">INTELLIGENCE</span>
            </div>
          )}
        </button>
        <Tooltip
          content={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          shortcut="["
          position="right"
        >
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </Tooltip>
      </div>

      <nav className="sidebar-nav" aria-label="Main navigation">
        {/* Primary Navigation - 7 items */}
        <div className="sidebar-section sidebar-section-primary">
          {PRIMARY_NAV.map(item => (
            <NavItemComponent
              key={item.path}
              item={item}
              collapsed={sidebarCollapsed}
              isActive={
                (item.path === '/research' && isResearchActive) ||
                (item.path === '/markets' && isMarketsActive) ||
                (item.path === '/intelligence' && isIntelligenceActive) ||
                (item.path === '/strategy' && isStrategyActive)
              }
            />
          ))}
        </div>

        {/* Spacer */}
        <div className="sidebar-nav-spacer" />

        {/* Utility Navigation - 3 items */}
        <div className="sidebar-section sidebar-section-utility">
          <div className="sidebar-nav-divider" />
          {UTILITY_NAV.map(item => (
            <NavItemComponent
              key={item.path}
              item={item}
              collapsed={sidebarCollapsed}
              isUtility={true}
            />
          ))}
        </div>
      </nav>

      <div className="sidebar-footer">
        <UserMenu collapsed={sidebarCollapsed} />
        <ConnectionStatusIndicator collapsed={sidebarCollapsed} />
        {!sidebarCollapsed && (
          <div className="sidebar-version">
            <span className="version-label">Cipher v1.0</span>
            <span className="version-env">Intelligence Engine</span>
          </div>
        )}
      </div>
    </aside>
  )
}
